import { useQuery } from "@tanstack/react-query";
import { useSupabaseConnection } from '@/hooks/useSupabaseConnection';
import type { Database } from "@/integrations/supabase/types";

type CourseImage = Database["public"]["Tables"]["course_images"]["Row"];

export const useCourseImages = (courseId: string) => {
  const { supabase } = useSupabaseConnection();
  return useQuery({
    queryKey: ["course-images", courseId],
    queryFn: async (): Promise<CourseImage[]> => {
      try {
        const { data, error } = await supabase
          .from("course_images")
          .select("*")
          .eq("course_id", courseId)
          .order("image_order", { ascending: true });

        if (error) {
          console.error("Error fetching course images:", error);
          throw new Error("Failed to fetch course images");
        }

        return data || [];
      } catch (error) {
        console.error('❌ 코스 이미지 조회 중 오류 발생:', error);
        
        // 타임아웃이나 네트워크 오류 시 빈 배열 반환
        if (error instanceof Error && error.message.includes('timeout')) {
          console.warn('⏱️ 쿼리 타임아웃 - 빈 배열 반환');
          return [];
        }
        throw error;
      }
    },
    enabled: !!courseId,
    staleTime: 1000 * 60 * 10, // 10분간 fresh 상태 유지
    gcTime: 1000 * 60 * 30, // 30분간 캐시 유지
    retry: (failureCount, error) => {
      // 연결 오류인 경우 재시도
      if (error && error.message && (error.message.includes('network') || error.message.includes('fetch'))) {
        return failureCount < 3;
      }
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // 지수 백오프
  });
};